import React from 'react';
import { Helmet } from 'react-helmet-async';
import { BRAND } from '../../config/brand';
import { normalizeBrandTitle } from '../../utils/brandTitle';

const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://kundlistarstudio.com').replace(/\/+$/, '');
const DEFAULT_IMAGE = `${SITE_URL}/logo192.png`;

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  path?: string;
  robots?: string;
  type?: string;
  image?: string;
}

const buildUrl = (path?: string) => {
  if (!path || path === '/') return `${SITE_URL}/`;
  if (/^https?:\/\//.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
};

const dedupeHeadTags = () => {
  [
    'meta[name="description"]',
    'meta[name="keywords"]',
    'meta[name="robots"]',
    'link[rel="canonical"]',
    'link[rel="alternate"][hreflang="x-default"]',
    'meta[property="og:title"]',
    'meta[property="og:description"]',
    'meta[property="og:url"]',
    'meta[property="og:image"]',
    'meta[name="twitter:title"]',
    'meta[name="twitter:description"]',
    'meta[name="twitter:image"]',
  ].forEach((selector) => {
    const nodes = Array.from(document.head.querySelectorAll(selector));
    nodes.slice(0, -1).forEach((node) => node.remove());
  });
};

const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  keywords,
  path,
  robots = 'index,follow',
  type = 'website',
  image = DEFAULT_IMAGE,
}) => {
  const canonical = buildUrl(path);
  const normalizedTitle = normalizeBrandTitle(title);

  React.useEffect(() => {
    dedupeHeadTags();
  }, [canonical, description, image, keywords, normalizedTitle, robots, type]);

  return (
    <Helmet>
      <title>{normalizedTitle}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:title" content={normalizedTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={normalizedTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="application-name" content={BRAND.name} />
      <meta name="apple-mobile-web-app-title" content={BRAND.name} />
    </Helmet>
  );
};

export default SeoHead;
